import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { invoicesAPI, clientsAPI } from "../lib/api";
import { formatCurrency, formatDate, getStatusColor, INVOICE_STATUSES } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Search, Receipt, ExternalLink, Plus } from "lucide-react";

export default function AllInvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter, statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (clientFilter !== "all") params.client_id = clientFilter;
      if (statusFilter !== "all") params.status = statusFilter;

      const [invoicesRes, clientsRes] = await Promise.all([
        invoicesAPI.list(params),
        clientsAPI.list(),
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || "-";

  const filteredInvoices = invoices.filter((invoice) =>
    invoice.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invoice.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats — per-currency totals
  const outstandingByCurrency = {};
  const collectedByCurrency = {};
  invoices.forEach((inv) => {
    const cur = inv.currency || "PHP";
    if (inv.status !== "paid") {
      const outstanding = Math.max((inv.total || 0) - (inv.paid_amount || 0), 0);
      outstandingByCurrency[cur] = (outstandingByCurrency[cur] || 0) + outstanding;
    }
    collectedByCurrency[cur] = (collectedByCurrency[cur] || 0) + (inv.paid_amount || 0);
  });
  const allCurrencies = [...new Set([
    ...Object.keys(outstandingByCurrency),
    ...Object.keys(collectedByCurrency),
  ])].sort();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="all-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">All Invoices</h1>
          <p className="text-muted-foreground mt-1">
            View and manage invoices across all clients
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Invoices</p>
            <p className="text-2xl font-bold mt-1">{invoices.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Outstanding per currency */}
      {Object.keys(outstandingByCurrency).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {allCurrencies.filter((cur) => outstandingByCurrency[cur] > 0).map((cur) => (
            <Card key={`outstanding-${cur}`}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding ({cur})</p>
                <p className="text-xl font-bold mt-1 font-mono text-yellow-600">
                  {formatCurrency(outstandingByCurrency[cur], cur)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Collected per currency */}
      {Object.keys(collectedByCurrency).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {allCurrencies.filter((cur) => collectedByCurrency[cur] > 0).map((cur) => (
            <Card key={`collected-${cur}`}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Collected ({cur})</p>
                <p className="text-xl font-bold mt-1 font-mono text-green-600">
                  {formatCurrency(collectedByCurrency[cur], cur)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-invoices-input"
          />
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[180px]" data-testid="client-filter">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {INVOICE_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No invoices found</h3>
            <p className="text-muted-foreground">
              {searchQuery || clientFilter !== "all" || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Create invoices from the client workspace"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                  <TableCell>{getClientName(invoice.client_id)}</TableCell>
                  <TableCell>{invoice.title}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(invoice.total, invoice.currency)}</TableCell>
                  <TableCell className="font-mono text-green-600">{formatCurrency(invoice.paid_amount || 0, invoice.currency)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                  </TableCell>
                  <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : "-"}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      data-testid={`view-invoice-${invoice.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
